// Block 233 (group 1) — one place that turns a failed request into a sentence.
//
// TWO CLIENT LAYERS, and a helper is only useful if it covers both:
//
//   api()      (src/api/client.js) THROWS an ApiError on any non-2xx, and ApiError's
//              own message is already `body.error || body.message || …`. So a catch
//              that does setError(e.message) has always worked — 80 sites already do.
//   apiFetch() (the client portal) and the bare fetch() in utils/ and services/ do
//              NOT throw and do NOT normalise. The caller holds a Response and must
//              read the body itself. No helper keyed off ApiError reaches those.
//
// messageFromError covers the first; messageFromResponse covers the second.
//
// It reads err.body rather than err.message deliberately, for two reasons: it is what
// index.js's global handler already did, so lifting that here is a no-op; and it is
// the only way to reach express-validator's shape, which ApiError.message cannot see.

// ~1395 of the backend's ~1433 error responses are { error: '…' }. 17 are
// express-validator's { errors: [{ msg }] } — POST /api/email/send among them — where
// ApiError.message falls back to "Request failed with status 400" because the real
// message is one level down. That gap is the main thing this helper closes.
function fromBody(body) {
  if (!body || typeof body !== 'object') return null;
  if (typeof body.error === 'string' && body.error.trim()) return body.error;
  if (typeof body.message === 'string' && body.message.trim()) return body.message;
  if (Array.isArray(body.errors) && body.errors.length) {
    const first = body.errors[0];
    if (typeof first === 'string' && first.trim()) return first;
    if (first && typeof first === 'object') {
      const m = first.msg || first.message;
      if (typeof m === 'string' && m.trim()) return m;
    }
  }
  return null;
}

/**
 * The message to show a user for a failed request.
 *
 * @param {unknown} err       an ApiError, a plain Error, or a parsed body
 * @param {string}  fallback  shown when nothing better can be found
 */
export function messageFromError(err, fallback = 'Something went wrong. Please try again.') {
  if (!err) return fallback;

  // ApiError carries the parsed body on .body; a raw parsed body works directly.
  const direct = fromBody(err.body) || fromBody(err);
  if (direct) return direct;

  // A status with no usable body. Wording preserved verbatim from the global handler
  // this replaces, so lifting it here changed nothing on screen.
  if (typeof err.status === 'number') return `Request failed (${err.status})`;

  // fetch() rejects with a TypeError when the network is down. "Failed to fetch" is
  // not a sentence for a user.
  if (err instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(err.message || '')) {
    return 'Could not reach the server. Check your connection and try again.';
  }

  if (typeof err.message === 'string' && err.message.trim()) return err.message;
  return fallback;
}

/**
 * The same, for the layer that does NOT throw — a Response from apiFetch or a bare
 * fetch. Reads the body once; a non-JSON body degrades to the status wording.
 *
 * Callers that need the parsed body anyway should read it themselves and pass it to
 * messageFromError, so the body is not consumed twice.
 */
export async function messageFromResponse(res, fallback) {
  if (!res) return messageFromError(null, fallback);
  const body = await res.json().catch(() => null);
  return messageFromError({ body, status: res.status }, fallback);
}
